import { Phone, Mail, MapPin, Clock, Shield, CheckCircle, MessageSquare, Instagram } from 'lucide-react';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';
import ContactForm from '../components/ContactForm';

const ContactPage = () => {
  const trustPoints = [
    'Free, no-obligation estimates',
    'Fully licensed and insured',
    'WCB covered for your protection',
    'Local Calgary contractor',
    'Transparent pricing — no hidden costs',
  ];

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-[#0f172a]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
            Get In Touch
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Contact Us
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
            Ready to start your renovation project? Get a free estimate or ask us any questions.
            We typically respond within 24 hours.
          </p>
          
          {/* Quick Contact Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:403-402-6596"
              className="inline-flex items-center justify-center gap-3 bg-[#c5a059] text-[#0f172a] px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-white transition-colors"
              data-testid="hero-call-btn"
            >
              <Phone size={18} />
              Call 403-402-6596
            </a>
            <a
              href="sms:403-402-6596"
              className="inline-flex items-center justify-center gap-3 border-2 border-white text-white px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-white hover:text-[#0f172a] transition-colors"
              data-testid="hero-text-btn"
            >
              <MessageSquare size={18} />
              Text Us
            </a>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-4 bg-[#0f172a] border-t border-[#1e293b]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-wrap justify-center gap-6 md:gap-10">
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <Shield className="w-4 h-4 text-[#c5a059]" />
              <span>Fully Insured</span>
            </div>
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <CheckCircle className="w-4 h-4 text-[#c5a059]" />
              <span>WCB Covered</span>
            </div>
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <MapPin className="w-4 h-4 text-[#c5a059]" />
              <span>Local Calgary</span>
            </div>
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <Clock className="w-4 h-4 text-[#c5a059]" />
              <span>Response within 24 hrs</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
                Contact Information
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a] mb-8">
                Let's Discuss Your Project
              </h2>
              <p className="text-slate-600 text-lg mb-8 leading-relaxed">
                Whether you're planning a basement development, bathroom renovation, or 
                custom feature installation, we're here to help. Reach out for a free, 
                no-obligation estimate.
              </p>

              {/* Contact Details */}
              <div className="space-y-6 mb-10">
                <a
                  href="tel:403-402-6596"
                  className="flex items-start gap-4 group"
                  data-testid="contact-phone-link"
                >
                  <div className="w-12 h-12 bg-[#f8f9fa] flex items-center justify-center group-hover:bg-[#0f172a] transition-colors">
                    <Phone size={20} className="text-[#c5a059]" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 uppercase tracking-wider">Call or Text</p>
                    <p className="text-lg font-semibold text-[#0f172a] group-hover:text-[#c5a059] transition-colors">
                      403-402-6596
                    </p>
                  </div>
                </a>

                <a
                  href="mailto:info@yyccustombasement.ca"
                  className="flex items-start gap-4 group"
                  data-testid="contact-email-link"
                >
                  <div className="w-12 h-12 bg-[#f8f9fa] flex items-center justify-center group-hover:bg-[#0f172a] transition-colors">
                    <Mail size={20} className="text-[#c5a059]" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 uppercase tracking-wider">Email</p>
                    <p className="text-lg font-semibold text-[#0f172a] group-hover:text-[#c5a059] transition-colors">
                      info@yyccustombasement.ca
                    </p>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#f8f9fa] flex items-center justify-center">
                    <MapPin size={20} className="text-[#c5a059]" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 uppercase tracking-wider">Location</p>
                    <p className="text-lg font-semibold text-[#0f172a]">
                      Calgary, Alberta
                    </p>
                    <p className="text-slate-600 text-sm">
                      Serving Calgary, Airdrie, Cochrane, Chestermere & Okotoks
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#f8f9fa] flex items-center justify-center">
                    <Clock size={20} className="text-[#c5a059]" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 uppercase tracking-wider">Hours</p>
                    <p className="text-lg font-semibold text-[#0f172a]">
                      Mon - Fri: 8am - 6pm
                    </p>
                    <p className="text-slate-600 text-sm">
                      Saturday: By Appointment
                    </p>
                  </div>
                </div>

                <a
                  href="https://www.instagram.com/yyccustombasement/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="flex items-start gap-4 group"
                  data-testid="contact-instagram-link"
                >
                  <div className="w-12 h-12 bg-[#f8f9fa] flex items-center justify-center group-hover:bg-[#0f172a] transition-colors">
                    <Instagram size={20} className="text-[#c5a059]" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 uppercase tracking-wider">Follow Us</p>
                    <p className="text-lg font-semibold text-[#0f172a] group-hover:text-[#c5a059] transition-colors">
                      @yyccustombasement
                    </p>
                  </div>
                </a>
              </div>

              {/* Why Choose Us Box */}
              <div className="bg-[#0f172a] p-6">
                <p className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#c5a059]" />
                  Why Choose Us
                </p>
                <ul className="space-y-2">
                  {trustPoints.map((point, index) => (
                    <li key={index} className="flex items-center gap-2 text-slate-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-[#c5a059] flex-shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-[#f8f9fa] p-8 md:p-10">
              <h3 className="font-heading text-2xl font-semibold text-[#0f172a] mb-2">
                Request a Free Estimate
              </h3>
              <p className="text-slate-600 text-sm mb-6 flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#c5a059]" />
                We respond within 24 hours
              </p>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="bg-[#f8f9fa] py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-8">
            <h3 className="font-heading text-2xl font-semibold text-[#0f172a] mb-2">
              Service Areas
            </h3>
            <p className="text-slate-600">
              We proudly serve Calgary and the surrounding communities
            </p>
          </div>
          <div className="bg-white p-8">
            <div className="flex flex-wrap justify-center gap-4">
              {[
                'Calgary',
                'Mahogany',
                'Auburn Bay',
                'Seton',
                'Legacy',
                'Airdrie',
                'Cochrane',
                'Chestermere',
                'Okotoks',
              ].map((area) => (
                <span
                  key={area}
                  className="px-4 py-2 bg-[#f8f9fa] text-[#0f172a] text-sm font-medium"
                >
                  {area}
                </span>
              ))}
            </div>
            <p className="text-center text-slate-500 text-sm mt-6">
              Don't see your area? Contact us — we may still be able to help!
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
